import React, { useEffect, useMemo, useState } from "react";
import {
    createCounterparty,
    createCounterpartyAccount,
    deleteCounterparty,
    deleteCounterpartyAccount,
    deleteCounterpartyDropshipLink,
    deleteCounterpartyWholesaleLink,
    deleteCounterpartyPlatformLink,
    listCounterparties,
    listPaymentPlatforms,
    updateCounterparty,
    upsertCounterpartyDropshipLink,
    upsertCounterpartyWholesaleLink,
    upsertCounterpartyPlatformLink,
} from "../api/counterpartiesApi";
import { getDropshipEnterprises } from "../api/dropshipEnterpriseApi";
import { listWholesaleSuppliers } from "../api/fulfillmentApi";

/* Styling comes from the scoped .suppliers-page design system (SuppliersPage.css);
   this panel always renders inside that wrapper. */

const emptyDraft = {
    legal_name: "",
    short_name: "",
    tax_id: "",
    counterparty_type: "tov",
    vat_payer: false,
    vat_enabled: false,
    address: "",
    phone: "",
    email: "",
    contact_person: "",
    is_active: true,
    notes: "",
};

const accountDraftDefault = {
    account_number: "",
    account_title: "",
    bank_name: "",
    mfo: "",
    currency: "UAH",
    is_default: false,
    is_active: true,
    notes: "",
};

const Field = ({ label, children }) => (
    <div className="sp-fld">
        <label>{label}</label>
        {children}
    </div>
);

const boolPill = (value) => <span className={value ? "sp-pill g" : "sp-pill r"}>{value ? "Да" : "Нет"}</span>;

const CounterpartiesPanel = () => {
    const [counterparties, setCounterparties] = useState([]);
    const [dropshipSuppliers, setDropshipSuppliers] = useState([]);
    const [wholesaleSuppliers, setWholesaleSuppliers] = useState([]);
    const [platforms, setPlatforms] = useState([]);
    const [selectedId, setSelectedId] = useState("");
    const [query, setQuery] = useState("");
    const [showOnlyActive, setShowOnlyActive] = useState(false);
    const [draft, setDraft] = useState(emptyDraft);
    const [accountDraft, setAccountDraft] = useState(accountDraftDefault);
    const [dropshipLinkDraft, setDropshipLinkDraft] = useState({ supplier_code: "", is_default: false });
    const [wholesaleLinkDraft, setWholesaleLinkDraft] = useState({ supplier_id: "", is_default: false });
    const [platformLinkDraft, setPlatformLinkDraft] = useState({ platform_code: "", is_default: false });
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState("");
    const [error, setError] = useState("");

    const selected = useMemo(
        () => counterparties.find((item) => String(item.id) === String(selectedId)) || null,
        [counterparties, selectedId],
    );
    const filteredCounterparties = useMemo(
        () => (showOnlyActive ? counterparties.filter((item) => item.is_active) : counterparties),
        [counterparties, showOnlyActive],
    );

    const loadBase = async () => {
        setLoading(true);
        setError("");
        try {
            const [counterpartyRows, dropshipRows, wholesaleRows, platformRows] = await Promise.all([
                listCounterparties({ query, limit: 500 }),
                getDropshipEnterprises(),
                listWholesaleSuppliers(),
                listPaymentPlatforms(),
            ]);
            setCounterparties(Array.isArray(counterpartyRows) ? counterpartyRows : []);
            setDropshipSuppliers(Array.isArray(dropshipRows) ? dropshipRows : []);
            setWholesaleSuppliers(Array.isArray(wholesaleRows) ? wholesaleRows : []);
            setPlatforms(Array.isArray(platformRows) ? platformRows : []);
            if (!selectedId && Array.isArray(counterpartyRows) && counterpartyRows[0]) {
                setSelectedId(String(counterpartyRows[0].id));
            }
        } catch (err) {
            setError(err?.response?.data?.detail || err.message || "Не удалось загрузить контрагентов.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadBase();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!selected) {
            setDraft(emptyDraft);
            return;
        }
        setDraft({
            legal_name: selected.legal_name || "",
            short_name: selected.short_name || "",
            tax_id: selected.tax_id || "",
            counterparty_type: selected.counterparty_type || "tov",
            vat_payer: Boolean(selected.vat_payer),
            vat_enabled: Boolean(selected.vat_enabled),
            address: selected.address || "",
            phone: selected.phone || "",
            email: selected.email || "",
            contact_person: selected.contact_person || "",
            is_active: Boolean(selected.is_active),
            notes: selected.notes || "",
        });
    }, [selected]);

    const setDraftField = (key, value) => setDraft((prev) => ({ ...prev, [key]: value }));

    const buildCounterpartyPayload = () => ({
        ...draft,
        legal_name: String(draft.legal_name || "").trim(),
        short_name: String(draft.short_name || "").trim() || null,
        tax_id: String(draft.tax_id || "").trim() || null,
        address: String(draft.address || "").trim() || null,
        phone: String(draft.phone || "").trim() || null,
        email: String(draft.email || "").trim() || null,
        contact_person: String(draft.contact_person || "").trim() || null,
        vat_enabled: Boolean(draft.vat_payer),
        notes: String(draft.notes || "").trim() || null,
    });

    const saveCounterparty = async () => {
        setError("");
        setMessage("");
        const payload = buildCounterpartyPayload();
        if (!payload.legal_name) {
            setError("Юридическое название обязательно.");
            return;
        }
        try {
            const saved = selected ? await updateCounterparty(selected.id, payload) : await createCounterparty(payload);
            setSelectedId(String(saved.id));
            setMessage("Контрагент сохранен.");
            await loadBase();
        } catch (err) {
            setError(err?.response?.data?.detail || err.message || "Не удалось сохранить контрагента.");
        }
    };

    const createNew = () => {
        setSelectedId("");
        setDraft(emptyDraft);
        setAccountDraft(accountDraftDefault);
        setMessage("");
        setError("");
    };

    const removeCounterparty = async () => {
        if (!selected) return;
        const label = selected.short_name || selected.legal_name || `#${selected.id}`;
        if (!window.confirm(`Удалить контрагента «${label}»? Действие необратимо.`)) return;
        setError("");
        setMessage("");
        try {
            await deleteCounterparty(selected.id);
            setSelectedId("");
            setDraft(emptyDraft);
            setMessage("Контрагент удален.");
            await loadBase();
        } catch (err) {
            setError(err?.response?.data?.detail || err.message || "Не удалось удалить контрагента.");
        }
    };

    const saveAccount = async () => {
        if (!selected) return;
        setError("");
        setMessage("");
        try {
            await createCounterpartyAccount(selected.id, {
                ...accountDraft,
                account_number: String(accountDraft.account_number || "").trim(),
                account_title: String(accountDraft.account_title || "").trim() || null,
                bank_name: String(accountDraft.bank_name || "").trim() || null,
                mfo: String(accountDraft.mfo || "").trim() || null,
                notes: String(accountDraft.notes || "").trim() || null,
            });
            setAccountDraft(accountDraftDefault);
            setMessage("Счет добавлен.");
            await loadBase();
        } catch (err) {
            setError(err?.response?.data?.detail || err.message || "Не удалось добавить счет.");
        }
    };

    const addDropshipLink = async () => {
        if (!selected) return;
        setError("");
        setMessage("");
        try {
            await upsertCounterpartyDropshipLink(selected.id, {
                supplier_code: dropshipLinkDraft.supplier_code,
                is_default: Boolean(dropshipLinkDraft.is_default),
                is_active: true,
                priority: dropshipLinkDraft.is_default ? 10 : 100,
            });
            setDropshipLinkDraft({ supplier_code: "", is_default: false });
            setMessage("Связь с отправителем сохранена.");
            await loadBase();
        } catch (err) {
            setError(err?.response?.data?.detail || err.message || "Не удалось связать отправителя.");
        }
    };

    const addWholesaleLink = async () => {
        if (!selected) return;
        setError("");
        setMessage("");
        try {
            await upsertCounterpartyWholesaleLink(selected.id, {
                supplier_id: Number(wholesaleLinkDraft.supplier_id),
                is_default: Boolean(wholesaleLinkDraft.is_default),
                is_active: true,
                priority: wholesaleLinkDraft.is_default ? 10 : 100,
            });
            setWholesaleLinkDraft({ supplier_id: "", is_default: false });
            setMessage("Связь с оптовым поставщиком сохранена.");
            await loadBase();
        } catch (err) {
            setError(err?.response?.data?.detail || err.message || "Не удалось связать оптового поставщика.");
        }
    };

    const addPlatformLink = async () => {
        if (!selected) return;
        setError("");
        setMessage("");
        try {
            await upsertCounterpartyPlatformLink(selected.id, {
                platform_code: platformLinkDraft.platform_code,
                is_default: Boolean(platformLinkDraft.is_default),
                is_active: true,
                priority: platformLinkDraft.is_default ? 10 : 100,
            });
            setPlatformLinkDraft({ platform_code: "", is_default: false });
            setMessage("Связь с площадкой сохранена.");
            await loadBase();
        } catch (err) {
            setError(err?.response?.data?.detail || err.message || "Не удалось связать площадку.");
        }
    };

    return (
        <div className="sp-split">
            <aside className="sp-card sp-picker">
                <h2>Контрагенты</h2>

                <div className="sp-picker-actions">
                    <button type="button" className="sp-btn sp-btn-primary sp-btn-sm" onClick={createNew}>+ Новый</button>
                    <button type="button" className="sp-btn sp-btn-ghost sp-btn-sm" onClick={loadBase}>Обновить</button>
                </div>

                <div style={{ display: "flex", gap: "8px" }}>
                    <input
                        className="sp-inp"
                        placeholder="Название, ЕДРПОУ, счёт…"
                        value={query}
                        onChange={(event) => setQuery(event.target.value)}
                        onKeyDown={(event) => { if (event.key === "Enter") loadBase(); }}
                    />
                    <button type="button" className="sp-btn sp-btn-ghost sp-btn-sm" onClick={loadBase}>Найти</button>
                </div>

                <label className="sp-switch-row">
                    <input type="checkbox" checked={showOnlyActive} onChange={(event) => setShowOnlyActive(event.target.checked)} />
                    <span>Только активные</span>
                </label>

                {loading ? <div className="sp-mini">Загрузка…</div> : null}

                <div className="sp-sender-list">
                    {filteredCounterparties.map((item) => (
                        <button
                            key={item.id}
                            type="button"
                            onClick={() => setSelectedId(String(item.id))}
                            className={String(item.id) === String(selectedId) ? "sp-sender-item sel" : "sp-sender-item"}
                        >
                            <span className="nm">{item.short_name || item.legal_name}</span>
                            <span className="meta">{item.tax_id || "ЕДРПОУ не указан"}</span>
                            <span className={item.is_active ? "sp-badge on" : "sp-badge off"}>
                                <span className="dot" />{item.is_active ? "Активен" : "Неактивен"}
                            </span>
                        </button>
                    ))}
                    {!loading && filteredCounterparties.length === 0 ? <div className="sp-mini">Контрагенты не найдены.</div> : null}
                </div>
            </aside>

            <section className="sp-detail">
                {error ? <div className="sp-card" style={{ padding: "12px 16px" }}><span className="sp-msg err">{error}</span></div> : null}
                {message ? <div className="sp-card" style={{ padding: "12px 16px" }}><span className="sp-msg ok">{message}</span></div> : null}

                <div className="sp-card">
                    <div className="sp-panel">
                        <h3>{selected ? "Карточка контрагента" : "Новый контрагент"}</h3>
                        <div className="sp-block">
                            <div className="sp-bh"><span className="ic">ⓘ</span><h4>Реквизиты</h4></div>
                            <div className="sp-grid3">
                                <Field label="Юридическое название"><input className="sp-inp" value={draft.legal_name} onChange={(event) => setDraftField("legal_name", event.target.value)} /></Field>
                                <Field label="Короткое имя"><input className="sp-inp" value={draft.short_name} onChange={(event) => setDraftField("short_name", event.target.value)} /></Field>
                                <Field label="ЕДРПОУ / ИНН"><input className="sp-inp" value={draft.tax_id} onChange={(event) => setDraftField("tax_id", event.target.value)} /></Field>
                                <Field label="Тип предприятия">
                                    <select className="sp-inp" value={draft.counterparty_type} onChange={(event) => setDraftField("counterparty_type", event.target.value)}>
                                        <option value="tov">ТОВ</option>
                                        <option value="fop_3">ФОП 3 группа</option>
                                        <option value="fop_2">ФОП 2 группа</option>
                                        <option value="individual">Физлицо</option>
                                        <option value="other">Другое</option>
                                    </select>
                                </Field>
                                <Field label="Телефон"><input className="sp-inp" value={draft.phone} onChange={(event) => setDraftField("phone", event.target.value)} /></Field>
                                <Field label="Email"><input className="sp-inp" value={draft.email} onChange={(event) => setDraftField("email", event.target.value)} /></Field>
                                <Field label="Контакт"><input className="sp-inp" value={draft.contact_person} onChange={(event) => setDraftField("contact_person", event.target.value)} /></Field>
                                <Field label="Адрес"><input className="sp-inp" value={draft.address} onChange={(event) => setDraftField("address", event.target.value)} /></Field>
                                <Field label="Заметки"><input className="sp-inp" value={draft.notes} onChange={(event) => setDraftField("notes", event.target.value)} /></Field>
                            </div>
                        </div>

                        <div className="sp-save-bar">
                            <button
                                type="button"
                                className={draft.vat_payer ? "sp-check ck" : "sp-check"}
                                onClick={() => setDraftField("vat_payer", !draft.vat_payer)}
                            >
                                <span className="box">{draft.vat_payer ? "✓" : ""}</span>
                                <span>Плательщик НДС</span>
                            </button>
                            <button
                                type="button"
                                className={draft.is_active ? "sp-check ck" : "sp-check"}
                                onClick={() => setDraftField("is_active", !draft.is_active)}
                            >
                                <span className="box">{draft.is_active ? "✓" : ""}</span>
                                <span>Активный</span>
                            </button>
                            <span className="note" />
                            {selected ? (
                                <button type="button" className="sp-btn sp-btn-soft-danger" onClick={removeCounterparty}>Удалить контрагента</button>
                            ) : null}
                            <button type="button" className="sp-btn sp-btn-primary" onClick={saveCounterparty}>Сохранить</button>
                        </div>
                    </div>
                </div>

                {selected && (
                    <>
                        <div className="sp-card">
                            <div className="sp-panel">
                                <h3>Счета</h3>
                                <div className="sp-block">
                                    <div className="sp-bh"><span className="ic">＋</span><h4>Добавить счёт</h4></div>
                                    <div className="sp-form-grid" style={{ gridTemplateColumns: "repeat(4, minmax(0, 1fr))" }}>
                                        <Field label="IBAN / номер счёта"><input className="sp-inp" value={accountDraft.account_number} onChange={(event) => setAccountDraft({ ...accountDraft, account_number: event.target.value })} /></Field>
                                        <Field label="Название счёта"><input className="sp-inp" value={accountDraft.account_title} onChange={(event) => setAccountDraft({ ...accountDraft, account_title: event.target.value })} /></Field>
                                        <Field label="Банк"><input className="sp-inp" value={accountDraft.bank_name} onChange={(event) => setAccountDraft({ ...accountDraft, bank_name: event.target.value })} /></Field>
                                        <Field label="МФО"><input className="sp-inp" value={accountDraft.mfo} onChange={(event) => setAccountDraft({ ...accountDraft, mfo: event.target.value })} /></Field>
                                        <Field label="Валюта"><input className="sp-inp" value={accountDraft.currency} onChange={(event) => setAccountDraft({ ...accountDraft, currency: event.target.value })} /></Field>
                                        <Field label="Примечание"><input className="sp-inp" value={accountDraft.notes} onChange={(event) => setAccountDraft({ ...accountDraft, notes: event.target.value })} /></Field>
                                        <button
                                            type="button"
                                            className={accountDraft.is_default ? "sp-check ck" : "sp-check"}
                                            onClick={() => setAccountDraft({ ...accountDraft, is_default: !accountDraft.is_default })}
                                        >
                                            <span className="box">{accountDraft.is_default ? "✓" : ""}</span>
                                            <span>Основной счёт</span>
                                        </button>
                                        <button type="button" className="sp-btn sp-btn-primary" style={{ alignSelf: "end" }} onClick={saveAccount}>Добавить счёт</button>
                                    </div>
                                </div>
                                <div className="sp-tbl-wrap">
                                    <table className="sp-tbl">
                                        <thead><tr><th>IBAN / счёт</th><th>Название</th><th>Банк</th><th>МФО</th><th>Валюта</th><th>Основной</th><th></th></tr></thead>
                                        <tbody>{(selected.accounts || []).map((account) => (
                                            <tr key={account.id}>
                                                <td>{account.account_number}</td>
                                                <td>{account.account_title || "—"}</td>
                                                <td>{account.bank_name || "—"}</td>
                                                <td>{account.mfo || "—"}</td>
                                                <td>{account.currency}</td>
                                                <td>{account.is_default ? boolPill(true) : ""}</td>
                                                <td><button type="button" className="sp-btn sp-btn-soft-danger sp-btn-sm" onClick={async () => { await deleteCounterpartyAccount(selected.id, account.id); await loadBase(); }}>Удалить</button></td>
                                            </tr>
                                        ))}
                                        {(selected.accounts || []).length === 0 ? <tr><td colSpan={7} className="sp-mini">Счетов пока нет.</td></tr> : null}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>

                        <div className="sp-card">
                            <div className="sp-panel">
                                <h3>Связанные отправители</h3>
                                <div className="sp-block">
                                    <div className="sp-bh"><span className="ic">⇄</span><h4>Связать отправителя</h4></div>
                                    <div className="sp-form-grid" style={{ gridTemplateColumns: "minmax(240px, 1fr) auto auto" }}>
                                        <div className="sp-fld">
                                            <label>Отправитель</label>
                                            <select className="sp-inp" value={dropshipLinkDraft.supplier_code} onChange={(event) => setDropshipLinkDraft({ ...dropshipLinkDraft, supplier_code: event.target.value })}>
                                                <option value="">Выберите отправителя</option>
                                                {dropshipSuppliers.map((supplier) => <option key={supplier.code} value={supplier.code}>{supplier.code} · {supplier.name}</option>)}
                                            </select>
                                        </div>
                                        <button
                                            type="button"
                                            className={dropshipLinkDraft.is_default ? "sp-check ck" : "sp-check"}
                                            onClick={() => setDropshipLinkDraft({ ...dropshipLinkDraft, is_default: !dropshipLinkDraft.is_default })}
                                        >
                                            <span className="box">{dropshipLinkDraft.is_default ? "✓" : ""}</span>
                                            <span>default</span>
                                        </button>
                                        <button type="button" className="sp-btn sp-btn-primary" style={{ alignSelf: "end" }} onClick={addDropshipLink}>Связать</button>
                                    </div>
                                </div>
                                <div className="sp-tbl-wrap">
                                    <table className="sp-tbl">
                                        <thead><tr><th>Код</th><th>Название</th><th>Default</th><th></th></tr></thead>
                                        <tbody>{(selected.dropship_links || []).map((link) => (
                                            <tr key={link.id}>
                                                <td>{link.supplier_code}</td>
                                                <td>{link.supplier_name || "—"}</td>
                                                <td>{link.is_default ? boolPill(true) : ""}</td>
                                                <td><button type="button" className="sp-btn sp-btn-soft-danger sp-btn-sm" onClick={async () => { await deleteCounterpartyDropshipLink(selected.id, link.id); await loadBase(); }}>Удалить</button></td>
                                            </tr>
                                        ))}
                                        {(selected.dropship_links || []).length === 0 ? <tr><td colSpan={4} className="sp-mini">Связей нет.</td></tr> : null}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>

                        <div className="sp-card">
                            <div className="sp-panel">
                                <h3>Связанные площадки</h3>
                                <div className="sp-block">
                                    <div className="sp-bh"><span className="ic">▦</span><h4>Связать площадку</h4></div>
                                    <p className="sp-mini" style={{ margin: "0 0 8px" }}>
                                        Платежи этого контрагента (оператора площадки — напр. ТОВ УАПРОМ → Prom) будут
                                        классифицироваться как «Платформы», а не как оплата поставщику.
                                    </p>
                                    <div className="sp-form-grid" style={{ gridTemplateColumns: "minmax(240px, 1fr) auto auto" }}>
                                        <div className="sp-fld">
                                            <label>Площадка</label>
                                            <select className="sp-inp" value={platformLinkDraft.platform_code} onChange={(event) => setPlatformLinkDraft({ ...platformLinkDraft, platform_code: event.target.value })}>
                                                <option value="">Выберите площадку</option>
                                                {platforms.map((platform) => <option key={platform.code} value={platform.code}>{platform.name}</option>)}
                                            </select>
                                        </div>
                                        <button
                                            type="button"
                                            className={platformLinkDraft.is_default ? "sp-check ck" : "sp-check"}
                                            onClick={() => setPlatformLinkDraft({ ...platformLinkDraft, is_default: !platformLinkDraft.is_default })}
                                        >
                                            <span className="box">{platformLinkDraft.is_default ? "✓" : ""}</span>
                                            <span>default</span>
                                        </button>
                                        <button type="button" className="sp-btn sp-btn-primary" style={{ alignSelf: "end" }} onClick={addPlatformLink}>Связать</button>
                                    </div>
                                </div>
                                <div className="sp-tbl-wrap">
                                    <table className="sp-tbl">
                                        <thead><tr><th>Код</th><th>Название</th><th>Default</th><th></th></tr></thead>
                                        <tbody>{(selected.platform_links || []).map((link) => (
                                            <tr key={link.id}>
                                                <td>{link.platform_code}</td>
                                                <td>{link.platform_name || "—"}</td>
                                                <td>{link.is_default ? boolPill(true) : ""}</td>
                                                <td><button type="button" className="sp-btn sp-btn-soft-danger sp-btn-sm" onClick={async () => { await deleteCounterpartyPlatformLink(selected.id, link.id); await loadBase(); }}>Удалить</button></td>
                                            </tr>
                                        ))}
                                        {(selected.platform_links || []).length === 0 ? <tr><td colSpan={4} className="sp-mini">Связей нет.</td></tr> : null}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>

                        <div className="sp-card">
                            <div className="sp-panel">
                                <h3>Связанные оптовые поставщики</h3>
                                <div className="sp-block">
                                    <div className="sp-bh"><span className="ic">⇄</span><h4>Связать оптового поставщика</h4></div>
                                    <div className="sp-form-grid" style={{ gridTemplateColumns: "minmax(240px, 1fr) auto auto" }}>
                                        <div className="sp-fld">
                                            <label>Оптовый поставщик</label>
                                            <select className="sp-inp" value={wholesaleLinkDraft.supplier_id} onChange={(event) => setWholesaleLinkDraft({ ...wholesaleLinkDraft, supplier_id: event.target.value })}>
                                                <option value="">Выберите оптового поставщика</option>
                                                {wholesaleSuppliers.map((supplier) => <option key={supplier.id} value={supplier.id}>{supplier.code} · {supplier.display_name || supplier.name}</option>)}
                                            </select>
                                        </div>
                                        <button
                                            type="button"
                                            className={wholesaleLinkDraft.is_default ? "sp-check ck" : "sp-check"}
                                            onClick={() => setWholesaleLinkDraft({ ...wholesaleLinkDraft, is_default: !wholesaleLinkDraft.is_default })}
                                        >
                                            <span className="box">{wholesaleLinkDraft.is_default ? "✓" : ""}</span>
                                            <span>default</span>
                                        </button>
                                        <button type="button" className="sp-btn sp-btn-primary" style={{ alignSelf: "end" }} onClick={addWholesaleLink}>Связать</button>
                                    </div>
                                </div>
                                <div className="sp-tbl-wrap">
                                    <table className="sp-tbl">
                                        <thead><tr><th>Код</th><th>Название</th><th>Default</th><th></th></tr></thead>
                                        <tbody>{(selected.wholesale_links || []).map((link) => (
                                            <tr key={link.id}>
                                                <td>{link.supplier_code || link.supplier_id}</td>
                                                <td>{link.supplier_name || "—"}</td>
                                                <td>{link.is_default ? boolPill(true) : ""}</td>
                                                <td><button type="button" className="sp-btn sp-btn-soft-danger sp-btn-sm" onClick={async () => { await deleteCounterpartyWholesaleLink(selected.id, link.id); await loadBase(); }}>Удалить</button></td>
                                            </tr>
                                        ))}
                                        {(selected.wholesale_links || []).length === 0 ? <tr><td colSpan={4} className="sp-mini">Связей нет.</td></tr> : null}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </section>
        </div>
    );
};

export default CounterpartiesPanel;
